<script setup lang="ts">
import { AssetType, SellingMethod } from '~/apiClient';

// COMPOSABLES
const { tt } = useTypedI18n();
useSeoMeta({
    title: tt('seo.trustReport.title'),
    ogTitle: tt('seo.trustReport.title'),
    description: tt('seo.trustReport.description'),
    ogDescription: tt('seo.trustReport.description'),
});
const { formatSlpRoute } = useSlpRouteUtils();
const localePath = useLocalePath();

// DATA
const carouselItems = [
    {
        title: '01.',
        description: tt('trustReport.sectionHowItWorks.item1'),
    },
    {
        title: '02.',
        description: tt('trustReport.sectionHowItWorks.item2'),
    },
    {
        title: '03.',
        description: tt('trustReport.sectionHowItWorks.item3'),
    },
    {
        title: '04.',
        description: tt('trustReport.sectionHowItWorks.item4'),
    },
    {
        title: '05.',
        description: tt('trustReport.sectionHowItWorks.item5'),
    },
];

// HANDLERS
const onHeroCtaClick = (): void => {
    scrollIntoView('what-is-the-trust-report');
};
</script>

<template>
    <div class="mb-12 flex flex-col gap-y-12 sm:mb-16 sm:gap-y-16 xl:mb-28 xl:gap-y-28">
        <BlockHeroLinedUp
            :media="{ type: 'YouTubeVideo', id: 'NlkQgTXlQNM', title: 'Fleequid Trust Report' }"
            :button="{ trailingIcon: 'lucide:move-down', label: tt('trustReport.hero.cta') }"
            @cta-click="onHeroCtaClick"
        >
            <template #title>
                <i18n-t keypath="trustReport.hero.title.base1" tag="span" scope="global">
                    <template #highlight>
                        <span class="text-primary"> {{ tt('trustReport.hero.title.highlight') }} </span>
                    </template>
                </i18n-t>
                <div>{{ tt('trustReport.hero.title.base2') }}</div>
            </template>

            <template #description>
                <div class="max-w-[960px]">
                    <i18n-t keypath="trustReport.hero.description1.base" tag="p" scope="global" class="font-normal">
                        <template #bold>
                            <span class="font-medium"> {{ tt('trustReport.hero.description1.bold') }} </span>
                        </template>
                    </i18n-t>
                    <p class="pb-8 font-normal">{{ tt('trustReport.hero.description2') }}</p>

                    <i18n-t keypath="trustReport.hero.description3.base" tag="p" scope="global" class="font-normal">
                        <template #highlight>
                            <span class="text-primary font-semibold"> {{ tt('trustReport.hero.description3.highlight') }} </span>
                        </template>
                    </i18n-t>
                </div>
            </template>
        </BlockHeroLinedUp>

        <BlockSectionFullWidth id="what-is-the-trust-report" :title="tt('trustReport.sectionWhatIs.title')" variant="gradient">
            <template #description>
                <i18n-t keypath="trustReport.sectionWhatIs.description1.base" tag="p" scope="global" class="pb-8 font-normal">
                    <template #bold>
                        <span class="font-semibold"> {{ tt('trustReport.sectionWhatIs.description1.bold') }} </span>
                    </template>
                </i18n-t>

                <i18n-t keypath="trustReport.sectionWhatIs.description2.base" tag="p" scope="global" class="font-normal">
                    <template #bold>
                        <span class="font-semibold"> {{ tt('trustReport.sectionWhatIs.description2.bold') }} </span>
                    </template>
                </i18n-t>
            </template>
        </BlockSectionFullWidth>

        <BlockSectionFullWidth variant="neutral">
            <template #title>
                <i18n-t keypath="trustReport.sectionHowItWorks.title.base" tag="p" scope="global" class="text-primary-800 text-3xl font-semibold">
                    <template #highlight>
                        <span class="text-primary"> {{ tt('trustReport.sectionHowItWorks.title.highlight') }} </span>
                    </template>
                </i18n-t>
            </template>

            <BlockCarousel :items="carouselItems" />
        </BlockSectionFullWidth>

        <BlockSectionWithSquareImage image="/trust-report/team-of-professionals.webp" layout-columns="4">
            <BlockList
                :title="tt('trustReport.sectionProfessionalTeam.title')"
                :items="[
                    tt('trustReport.sectionProfessionalTeam.item1'),
                    tt('trustReport.sectionProfessionalTeam.item2'),
                    tt('trustReport.sectionProfessionalTeam.item3'),
                ]"
            >
                <template #description>
                    <i18n-t keypath="trustReport.sectionProfessionalTeam.description.base" tag="p" scope="global">
                        <template #highlight>
                            <span class="text-primary font-semibold"> {{ tt('trustReport.sectionProfessionalTeam.description.highlight') }} </span>
                        </template>
                    </i18n-t>
                </template>
            </BlockList>
        </BlockSectionWithSquareImage>

        <BlockSectionWithSquareImage image="/trust-report/history-analysis.webp" layout-columns="4" collapse-image>
            <BlockList
                :title="tt('trustReport.sectionHistoryAnalysis.title')"
                :description="tt('trustReport.sectionHistoryAnalysis.description')"
                :items="[
                    tt('trustReport.sectionHistoryAnalysis.item1'),
                    tt('trustReport.sectionHistoryAnalysis.item2'),
                    tt('trustReport.sectionHistoryAnalysis.item3'),
                    tt('trustReport.sectionHistoryAnalysis.item4'),
                ]"
            />
        </BlockSectionWithSquareImage>

        <BlockSectionWithSquareImage image="/trust-report/body-interiors.webp" layout-columns="4" collapse-image>
            <BlockList
                :title="tt('trustReport.sectionBodyInteriors.title')"
                :description="tt('trustReport.sectionBodyInteriors.description')"
                :items="[
                    tt('trustReport.sectionBodyInteriors.item1'),
                    tt('trustReport.sectionBodyInteriors.item2'),
                    tt('trustReport.sectionBodyInteriors.item3'),
                    tt('trustReport.sectionBodyInteriors.item4'),
                ]"
            />
        </BlockSectionWithSquareImage>

        <BlockSectionWithSquareImage image="/trust-report/general-state.webp" layout-columns="4" collapse-image>
            <BlockList
                :title="tt('trustReport.sectionGeneralState.title')"
                :description="tt('trustReport.sectionGeneralState.description')"
                :items="[
                    tt('trustReport.sectionGeneralState.item1'),
                    tt('trustReport.sectionGeneralState.item2'),
                    tt('trustReport.sectionGeneralState.item3'),
                    tt('trustReport.sectionGeneralState.item4'),
                    tt('trustReport.sectionGeneralState.item5'),
                ]"
            />
        </BlockSectionWithSquareImage>

        <BlockSectionFullWidth
            :button="{
                trailingIcon: 'lucide:chevron-right',
                label: tt('trustReport.discoverAllVehiclesCta'),
                to: localePath(formatSlpRoute({ sellingMethod: SellingMethod.Auction, assetType: AssetType.Bus })),
            }"
            variant="white"
        />

        <BlockSection class="grid gap-8 space-y-0 lg:grid-cols-2">
            <BlockList
                class="bg-neutral-50 p-6"
                space="5"
                :description="tt('trustReport.sectionBuyerBenefits.description')"
                :items="[
                    tt('trustReport.sectionBuyerBenefits.item1'),
                    tt('trustReport.sectionBuyerBenefits.item2'),
                    tt('trustReport.sectionBuyerBenefits.item3'),
                    tt('trustReport.sectionBuyerBenefits.item4'),
                    tt('trustReport.sectionBuyerBenefits.item5'),
                ]"
            >
                <template #title>
                    <div class="p-2">{{ tt('trustReport.sectionBuyerBenefits.title') }}</div>
                </template>
            </BlockList>

            <BlockList
                class="bg-neutral-50 p-6"
                space="5"
                :items="[
                    tt('trustReport.sectionSellerBenefits.item1'),
                    tt('trustReport.sectionSellerBenefits.item2'),
                    tt('trustReport.sectionSellerBenefits.item3'),
                    tt('trustReport.sectionSellerBenefits.item4'),
                    tt('trustReport.sectionSellerBenefits.item5'),
                ]"
            >
                <template #title>
                    <div class="p-2">{{ tt('trustReport.sectionSellerBenefits.title') }}</div>
                </template>
                <template #description>
                    <i18n-t keypath="trustReport.sectionSellerBenefits.description.base" tag="p" scope="global">
                        <template #highlight>
                            <span class="text-primary font-semibold"> {{ tt('trustReport.sectionSellerBenefits.description.highlight') }} </span>
                        </template>
                    </i18n-t>
                </template>
            </BlockList>
        </BlockSection>

        <BlockSectionFullWidth
            :title="tt('trustReport.sectionStandardEnhancement.title')"
            :button="{
                trailingIcon: 'lucide:chevron-right',
                label: tt('trustReport.sectionStandardEnhancement.cta'),
                to: localePath(formatSlpRoute({ sellingMethod: SellingMethod.DirectSale, assetType: AssetType.Bus })),
            }"
            variant="neutral"
        >
            <template #description>
                <i18n-t keypath="trustReport.sectionStandardEnhancement.description1.base" tag="p" scope="global" class="pb-8 font-normal">
                    <template #highlight>
                        <span class="text-primary font-semibold"> {{ tt('trustReport.sectionStandardEnhancement.description1.highlight') }} </span>
                    </template>
                </i18n-t>

                <i18n-t keypath="trustReport.sectionStandardEnhancement.description2.base" tag="p" scope="global" class="font-normal">
                    <template #highlight>
                        <span class="text-primary font-semibold"> {{ tt('trustReport.sectionStandardEnhancement.description2.highlight') }} </span>
                    </template>
                </i18n-t>
            </template>
        </BlockSectionFullWidth>

        <BlockSection :title="tt('trustReport.faq.title')">
            <BaseAccordion
                size="xl"
                :items="[
                    { label: tt('trustReport.faq.items.item1.title'), content: tt('trustReport.faq.items.item1.content') },
                    { label: tt('trustReport.faq.items.item2.title'), content: tt('trustReport.faq.items.item2.content') },
                    { label: tt('trustReport.faq.items.item3.title'), content: tt('trustReport.faq.items.item3.content') },
                    { label: tt('trustReport.faq.items.item4.title'), content: tt('trustReport.faq.items.item4.content') },
                ]"
            />
        </BlockSection>
    </div>
</template>
